import {
  MONTAJ_DURUM_SOZU,
  MONTAJ_OLCUM_HATLARI,
  MONTAJ_OLCUM_TIPLERI,
  MONTAJ_SATIR_DURUMLARI,
  SET_HATLARI,
  fm12Satiri,
  fm12SatirSayisi,
  montajRnBul,
  type Fm12Basimi,
  type MontajAkisBilgisi,
  type MontajBelgeCipi,
  type MontajBirimYaniti,
  type MontajHali,
  type MontajIsGorunumu,
  type MontajKaydiYaniti,
  type MontajListeSatiri,
  type MontajListesi,
  type MontajOkuma,
  type MontajOkumaFormu,
  type MontajOlcumHatti,
  type MontajOlcumTipi,
  type MontajOlcumYaniti,
  type MontajOnay,
  type MontajRaporUyumu,
  type MontajRiserYaniti,
  type MontajSatirDurumu,
  type MontajSatirYaniti,
  type SayfaAraligi,
} from "@oasis/domain";
import { api } from "@/lib/api";
import { cevir } from "@/lib/dil";
import { EXCEL_ALANI, PDF_ALANI, TOPLU_UCU, raporAdi } from "@/lib/montaj-toplu";
import { kisaTarihSaat, saat, type Donusum, type Ton } from "@/lib/mrb-toplu";

/**
 * D-068: riserın Assembly sayfasının (Montaj.tsx), Finishing'deki "Assembly records" listesinin ve "Assembly report converter"
 * kutusunun (MontajKuyruk.tsx, Assembly.tsx) saf mantığı. Ekranlar yalnız çizer: sayfa hâli, etiketler, durum ve onay cümleleri,
 * iki adımlı akışların sırası ve sunucu uçlarının adları burada. Yanıtın ham alanlarına bu dosyadaki fonksiyonlar dışında
 * dokunulmaz. JSX yok; React ve oturum katmanını yüklemez (dosya yüklemesi için yalnız isteğin parçalarını verir, `dosyaYukle`'yi
 * ekran çağırır). Tipler alan paketindedir (`@oasis/domain`: `MontajRiserYaniti`, `MontajListesi`, …).
 * Düğmelerin sunulup sunulmayacağını sunucu söyler (`kuyrugaEklenebilir`, `girdisizTamamlanabilir`, `kaldirilabilir`); burada
 * onlar için kural yoktur. Sunucu cümleleri İngilizce gelir ve burada `cevir` ile çevrilir.
 */

/* ------------------------------------------------------------ tipler ------------------------------------------------------------ */

/** Sayfanın hâli: kayıt yokken üç hâl, kayıt varken kaydın hâli. */
export type SayfaHali = "akis_disi" | "rapor_bekliyor" | "rapor_yuklu" | MontajHali;
/** Kaydın raporu ile Finishing bağının uyumu (sunucu hesaplar). */
export type RaporUyumu = MontajRaporUyumu;
export interface EtiketGorunumu {
  metin: string;
  ton: Ton;
}

/** `POST /api/montaj/riser/:rn/ac` yanıtı. `kuyrukEngeli` yalnız kayıt bu istekle açıldıysa ve sıraya giremediyse doludur. */
export interface MontajAcYaniti {
  id: string;
  /** acildi: kayıt bu istekle açıldı · vardi: riserın kaydı zaten bu rapordan açılmıştı */
  sonuc: "acildi" | "vardi";
  kuyrugaGirdi: boolean;
  siraNo: number | null;
  kuyrukEngeli: string | null;
}
/** `POST /api/montaj/:id/girdi` yanıtı. */
export interface MontajGirdiYaniti {
  ok: true;
  ozet: { satir: number; birim: number; celiski: number; eksikSayfa: number };
  uyarilar: string[];
  /** bekleyen kayıt bu okumayla tamamlandı */
  tamamlandi: boolean;
  /** kaydın dönüşüm işi düştü */
  siradanCikti: boolean;
}
/** `POST /api/montaj/:id/sira` yanıtı: işin yeni görünümü (tarihler ISO metin). */
export type MontajSiraYaniti = { ok: true } & MontajIsGorunumu;
/** `POST /api/montaj/:id/kaldir` yanıtı. `akisBagi`: Finishing bağı kaldırılan kaydın raporunu gösteriyor. */
export interface MontajKaldirYaniti {
  ok: true;
  rn: string;
  akisBagi: boolean;
}

/* ------------------------------------------------------------- uçlar ------------------------------------------------------------- */

/** Sunucu uçları (`/api` önekini `lib/api.ts` ve `dosyaYukle` ekler). Son üçü var olan Finishing uçlarıdır; değişmediler. */
export const MONTAJ_UCU = {
  /** GET: "Assembly records" listesi ve dönüştürücü kutusu (`MontajListesi`) */
  liste: "/montaj",
  /** GET: riserın Assembly sayfası (`MontajRiserYaniti`) */
  riser: (rn: string) => `/montaj/riser/${rn}`,
  /** POST: Finishing'deki rapordan kayıt açar (`MontajAcYaniti`) */
  ac: (rn: string) => `/montaj/riser/${rn}/ac`,
  /** POST form-data `pdf`, `excel`, `rn`: toplu pencerenin tek satırı ve arşiv yüklemesi (`MontajTopluYaniti`) */
  toplu: TOPLU_UCU,
  /** POST form-data `excel`: assembly girdisi (`MontajGirdiYaniti`) */
  girdi: (id: string) => `/montaj/${id}/girdi`,
  /** POST `{ islem: "ekle" | "cikar" }` (`MontajSiraYaniti`) */
  sira: (id: string) => `/montaj/${id}/sira`,
  /** POST: "Proceed without excel" */
  girdisiz: (id: string) => `/montaj/${id}/girdisiz`,
  /** POST: kaydı kaldırır (`MontajKaldirYaniti`) */
  kaldir: (id: string) => `/montaj/${id}/kaldir`,
  /** POST form-data `dosya` (+ `kismi`, `sayfalar`): akıştaki riserın Assembly raporu, bütün kapılarıyla */
  asmRapor: (rn: string) => `/assembly/${rn}/rapor/asm`,
  /** POST: Finishing'deki Assembly raporu bağını (ve yanındaki excel ekini) kaldırır */
  asmRaporKaldir: (rn: string) => `/assembly/${rn}/rapor/asm/kaldir`,
  /** POST: yalnız okunmayan eski excel ekini kaldırır */
  asmExcelKaldir: (rn: string) => `/assembly/${rn}/rapor/asm/excel/kaldir`,
} as const;

/** `dosyaYukle(yol, dosya, alanlar, ekDosyalar, dosyaAlan)` çağrısının parçaları. */
export interface YuklemeIstegi<D> {
  yol: string;
  dosya: D;
  alanlar: Record<string, string>;
  ekDosyalar: Record<string, D>;
  dosyaAlan: string;
}

// Üç istek kurucusu `function` bildirimidir, ok fonksiyonu değil: sözlük üreticisi (docs/ceviri/sozluk.mjs) her dosyayı TSX
// olarak ayrıştırır ve `= <D>(…) =>` yazımını JSX etiketi sanar; dosyanın geri kalanındaki `cevir("…")` anahtarlarını göremez.

/** Akıştaki riserın Assembly raporu: var olan uç, bugünkü alan adı (`dosya`). `sayfaAlanlari`: "Partial" seçiminin form alanları. */
export function akisRaporIstegi<D>(rn: string, dosya: D, sayfaAlanlari: Record<string, string> = {}): YuklemeIstegi<D> {
  return { yol: MONTAJ_UCU.asmRapor(rn), dosya, alanlar: sayfaAlanlari, ekDosyalar: {}, dosyaAlan: "dosya" };
}
/** Kuyrukta olmayan riserın arşiv raporu: toplu ucun tek satırı; riser sayfanın riserıdır (dosya adından okunmaz). */
export function arsivRaporIstegi<D>(rn: string, pdf: D): YuklemeIstegi<D> {
  return { yol: MONTAJ_UCU.toplu, dosya: pdf, alanlar: { rn }, ekDosyalar: {}, dosyaAlan: PDF_ALANI };
}
/** Kayda assembly girdi excel'i ("Upload assembly input (excel)" ve "Replace assembly input (excel)"). */
export function girdiIstegi<D>(id: string, excel: D): YuklemeIstegi<D> {
  return { yol: MONTAJ_UCU.girdi(id), dosya: excel, alanlar: {}, ekDosyalar: {}, dosyaAlan: EXCEL_ALANI };
}

/* ------------------------------------------------------------ biçim ------------------------------------------------------------ */

/** Sayfa aralığı cümle içinde: "4–6" ya da "9" (sunucudaki `montajSayfaYazimi` ile aynı). */
export const sayfaAraligiMetni = (a: SayfaAraligi): string => (a.sayfaBas === a.sayfaSon ? String(a.sayfaBas) : `${a.sayfaBas}–${a.sayfaSon}`);

/** Eksik taranmış sayfalar: [2] → "2" · [2, 3] → "2, 3". */
export const eksikSayfaMetni = (sayfalar: readonly number[]): string => sayfalar.join(", ");

/** Kuyruğa giriş zamanı: bugünse yalnız saat, değilse kısa tarih ve saat (söküm kaydındaki gösterimle aynı). */
const siraZamani = (ts: string, simdi: Date): string => (new Date(ts).toDateString() === simdi.toDateString() ? saat(ts) : kisaTarihSaat(ts));

/** Dönüştürülen işin tutma süresi: "since 14:47 · held until 15:17"; alanlar boşsa boş metin. */
export function tutmaMetni(t: { tutmaBaslangic: string | null; tutmaBitis: string | null } | null): string {
  if (!t) return "";
  return [t.tutmaBaslangic ? cevir("since {0}", saat(t.tutmaBaslangic)) : null, t.tutmaBitis ? cevir("held until {0}", saat(t.tutmaBitis)) : null].filter(Boolean).join(" · ");
}

/* ----------------------------------------------- sayfa hâli ve riser satırı etiketleri ----------------------------------------------- */

/**
 * Riserın Assembly sayfasının hâli. Kayıt varsa kaydın hâlidir. Kayıt yokken: Finishing'de rapor yüklüyse "rapor_yuklu" (gönderim
 * geri alınmış olsa da); değilse riser kuyruktaysa "rapor_bekliyor" (var olan uç ve kapıları), kuyrukta değilse "akis_disi" (arşiv yolu).
 */
export function sayfaHali(v: Pick<MontajRiserYaniti, "kayit" | "akis" | "yukleme">): SayfaHali {
  if (v.kayit) return v.kayit.hal;
  if (v.akis.asmRaporu) return "rapor_yuklu";
  return v.yukleme === "akis" ? "rapor_bekliyor" : "akis_disi";
}

/** Kaydın hâl etiketi (riser satırında ve sayfa başlığında). */
function kayitEtiketi(k: { hal: MontajHali; dolu: number; toplam: number }): EtiketGorunumu {
  switch (k.hal) {
    case "girdi_bekliyor":
      return { metin: cevir("assembly input expected"), ton: "warn" };
    case "okundu":
      return { metin: cevir("rows read"), ton: "ok" };
    case "girdisiz":
      return { metin: cevir("not read"), ton: "neutral" };
    case "form_acik":
      return { metin: cevir("form {0}/{1} rows", k.dolu, k.toplam), ton: "info" };
    case "imzali_bekliyor":
      return { metin: cevir("signed print-out expected"), ton: "warn" };
    case "form_kapali":
      return { metin: cevir("form closed"), ton: "ok" };
  }
}

/**
 * Finishing'deki riser satırının ve riserın Assembly sayfası başlığının etiketleri. Sıra: akış etiketi, arşiv / başka dosya etiketi,
 * kayıt etiketi. "report uploaded" yalnız Finishing bağı (`set_durum.asm_raporu`) doluyken çıkar: adımı tamamlayan o bağdır, arşiv
 * kaydı değil. Gönderilmemiş riserda akış etiketi yoktur (satırın üstünde zaten "not queued …" yazar); ekranda doldurulan form
 * sürerken "report expected" çıkmaz (formun etiketi konuşur). `kayit`: liste satırı (`MontajListeSatiri`) doğrudan verilebilir.
 */
export function satirEtiketleri(
  akis: { gonderildi: boolean; asmVar: boolean },
  kayit: { hal: MontajHali; tur: "rapor" | "form"; raporUyumu: RaporUyumu; dolu: number; toplam: number } | null,
): EtiketGorunumu[] {
  const etiketler: EtiketGorunumu[] = [];
  const formSuruyor = kayit?.hal === "form_acik" || kayit?.hal === "imzali_bekliyor";
  if (akis.gonderildi) {
    if (akis.asmVar) etiketler.push({ metin: cevir("report uploaded"), ton: "ok" });
    else if (!formSuruyor) etiketler.push({ metin: cevir("report expected"), ton: "warn" });
  }
  if (!kayit) {
    if (akis.asmVar) etiketler.push({ metin: cevir("not read"), ton: "neutral" });
    return etiketler;
  }
  if (kayit.tur === "rapor" && (kayit.raporUyumu === "akis_disi" || kayit.raporUyumu === "bag_yok")) etiketler.push({ metin: cevir("archive record"), ton: "neutral" });
  if (kayit.raporUyumu === "farkli") etiketler.push({ metin: cevir("record of another file"), ton: "warn" });
  etiketler.push(kayitEtiketi(kayit));
  return etiketler;
}

/** Riserın Assembly sayfası başlığının etiketleri: Finishing satırındakiyle aynı söz (`satirEtiketleri`). */
export function riserEtiketleri(v: Pick<MontajRiserYaniti, "akis" | "kayit" | "raporUyumu">): EtiketGorunumu[] {
  const k = v.kayit;
  return satirEtiketleri(
    { gonderildi: v.akis.gonderildi, asmVar: Boolean(v.akis.asmRaporu) },
    k && { hal: k.hal, tur: k.tur, raporUyumu: v.raporUyumu, dolu: k.doluluk.dolu, toplam: k.doluluk.toplam },
  );
}

/** Etiketlerin yanında kuyruk etiketi (queued · n / converting / failed) çizilir mi: girdisi beklenen kaydın işi varsa. */
export const kuyrukEtiketiVar = (k: { hal: MontajHali; donusum: Donusum | null } | null | undefined): boolean => Boolean(k && k.hal === "girdi_bekliyor" && k.donusum);

/** Dönüştürücü kaydın üstünde çalışıyor ya da sırası gelecek: sayfa kendiliğinden tazelenir (teslim gelince dolsun). */
export const kuyrukSuruyor = (k: { donusum: Donusum | null } | null | undefined): boolean => k?.donusum === "sirada" || k?.donusum === "donusturuluyor";

/** Sessiz yenileme aralığı (söküm listesi ve MRB intake ile aynı). */
export const MONTAJ_YENILEME_MS = 30_000;

/* -------------------------------------------- "Assembly records" listesi ve dönüştürücü kutusu -------------------------------------------- */

/** "Form fullness" hücresi: form kaydında "22/30 rows", okunmuş kayıtta "30/30 rows (read)", öteki hâllerde "—". */
export function dolulukMetni(s: Pick<MontajListeSatiri, "tur" | "hal" | "dolu" | "toplam">): string {
  if (s.tur === "form") return `${s.dolu}/${s.toplam} ${cevir("rows")}`;
  return s.hal === "okundu" ? `${s.dolu}/${s.toplam} ${cevir("rows (read)")}` : "—";
}

export interface ListeDurumu {
  etiket: EtiketGorunumu;
  /** Okunmuş kayıtta girdinin kaynağı: "converter · 10/10, 13:48" ya da "by hand"; öteki hâllerde null. */
  kaynak: string | null;
  /** İşin durumu ve düğmesi nerede çizilir: etiketin yanında · alt satırda ("assembly input" sözüyle) · çizilmez. */
  kuyruk: "etiketle" | "alt_satir" | "yok";
  /** Girdisiz kayıtta işi yokken gri "completed without the assembly input". */
  girdisizNotu: boolean;
  /** Ek satır: "archive record" (gri, ton neutral) ya da "record of another file" (warn); yoksa null. */
  uyum: EtiketGorunumu | null;
}

/** "Status" hücresi. Okunmuş kaydın işi yalnız yeniden sıraya konduysa görünür; girdisiz kayıtta iş durumu ("none" dahil) hep görünür. */
export function listeDurumu(s: MontajListeSatiri): ListeDurumu {
  const uyum: EtiketGorunumu | null = s.raporUyumu === "farkli"
    ? { metin: cevir("record of another file"), ton: "warn" }
    : s.tur === "rapor" && (s.raporUyumu === "akis_disi" || s.raporUyumu === "bag_yok") ? { metin: cevir("archive record"), ton: "neutral" } : null;
  const yalin = { kaynak: null, kuyruk: "yok" as const, girdisizNotu: false, uyum };
  switch (s.hal) {
    case "girdi_bekliyor":
      return { ...yalin, etiket: { metin: cevir("assembly input expected"), ton: "warn" }, kuyruk: "etiketle" };
    case "okundu":
      return {
        ...yalin,
        etiket: { metin: cevir("rows read"), ton: "ok" },
        kaynak: s.girdi?.kaynak === "donusturucu" ? `${cevir("converter")}${s.girdi.ts ? ` · ${kisaTarihSaat(s.girdi.ts)}` : ""}` : cevir("by hand"),
        kuyruk: s.donusum ? "alt_satir" : "yok",
      };
    case "girdisiz":
      return { ...yalin, etiket: { metin: cevir("report (PDF)"), ton: "ok" }, kuyruk: "alt_satir", girdisizNotu: !s.donusum };
    case "form_acik":
      return { ...yalin, etiket: { metin: cevir("ongoing"), ton: "info" } };
    case "imzali_bekliyor":
      return { ...yalin, etiket: { metin: cevir("signed print-out expected"), ton: "warn" } };
    case "form_kapali":
      return { ...yalin, etiket: { metin: cevir("closed"), ton: "ok" } };
  }
}

/**
 * İşin durumunun yanındaki düğme: sıradaki iş çıkarılır ("Remove from queue"; bekleyen kayıtta da, hiçbir riser bu excel'i beklerken
 * tutulmaz) · dönüştürülen işin düğmesi yoktur · hatadaki iş "Retry" alır · işi olmayan kayıt yalnız sunucu sunuyorsa "Add to queue".
 * `okundu`: girdisi okunmuş kayıt. Hatadaki işi "Retry" almaz: okunmuş kaydı yeniden sıraya koyan düğme hiçbir yerde çizilmez
 * (liste ve sayfa aynı kuralı okur); düzeltme yolu yeni girdinin yüklenmesidir. Sıradaki işi yine çıkarılabilir.
 */
export function kuyrukDugmesi(k: { donusum: Donusum | null }, eklenebilir: boolean, okundu = false): "ekle" | "tekrar" | "cikar" | null {
  switch (k.donusum) {
    case "sirada":
      return "cikar";
    case "donusturuluyor":
      return null;
    case "hata":
      return okundu ? null : "tekrar";
    default:
      return eklenebilir ? "ekle" : null;
  }
}

/**
 * "Now converting" hücresi: dönüştürücünün şu an tuttuğu belgenin adı, hangi türde olursa olsun; boştaysa boş metin. Riser `rn`
 * alanından, yoksa `ad` alanından okunur (bu kutunun yanıtı `ad`, söküm ve rapor kutularınınki `rn` / `sn` taşır).
 */
export function donusturulenAdi(t: { tur: string; rn?: string; sn?: string; ad?: string } | null): string {
  if (!t) return "";
  const riser = t.rn ?? t.ad ?? "";
  switch (t.tur) {
    case "assembly":
      return raporAdi(riser);
    case "sokum":
      return cevir("Disassembly report {0}", riser);
    case "disposition":
      return cevir("Disposition report {0}", riser);
    case "rework":
      return cevir("Rework report {0}", riser);
    case "mrb":
      return `MRB ${t.sn ?? t.ad ?? ""}`;
    case "hidrotest":
      return cevir("Hydrotest certificate {0}", t.ad ?? t.rn ?? "");
    // D-074 Faz 2 · PR 9: NDT raporu okuma işi koduyla ("NDT report NDTR-7C41D2A0").
    case "ndt":
      return cevir("NDT report {0}", t.ad ?? t.rn ?? "");
    // D-074 Faz 2 · PR 10: weld map okuma işi koduyla ("Weld map WMAP-5D20B7E1").
    case "weldmap":
      return cevir("Weld map {0}", t.ad ?? t.rn ?? "");
    // D-074 Faz 2 · PR 11: PWHT grafiği okuma işi koduyla ("PWHT chart PWHT-5C11A0E2").
    case "pwht":
      return cevir("PWHT chart {0}", t.ad ?? t.rn ?? "");
    // D-074 Faz 3 · PR 14: NDT personel sertifikası okuma işi koduyla ("NDT certificate NDTC-0C9D8E7F").
    case "ndt_personel":
      return cevir("NDT certificate {0}", t.ad ?? t.rn ?? "");
    // D-074 Faz 3 · PR 15: kaynakçı belgesi okuma işi koduyla ("Welder document WPQR-0C9D8E7F").
    case "kaynakci_wpq":
      return cevir("Welder document {0}", t.ad ?? t.rn ?? "");
    // D-074 Faz 3 · PR 16: kalibrasyon sertifikası okuma işi koduyla ("Calibration certificate CAL-0C9D8E7F").
    case "alet_kalibrasyon":
      return cevir("Calibration certificate {0}", t.ad ?? t.rn ?? "");
    // D-074 Faz 3 · PR 17: sarf belgesi okuma işi koduyla ("Consumable document CONS-0C9D8E7F").
    case "sarf_belgesi":
      return cevir("Consumable document {0}", t.ad ?? t.rn ?? "");
    // D-074 Faz 3 · PR 18: WPS belgesi okuma işi koduyla ("WPS document PROC-0C9D8E7F").
    case "wps_belgesi":
      return cevir("WPS document {0}", t.ad ?? t.rn ?? "");
    // D-074 Faz 4 · PR 20: packing list okuma işi koduyla ("Packing list PL-0C9D8E7F").
    case "teslim_listesi":
      return cevir("Packing list {0}", t.ad ?? t.rn ?? "");
    // D-074 Faz 4 · PR 21: malzeme sertifikası okuma işi koduyla ("Material certificate MTC-0C9D8E7F").
    case "malzeme_sertifikasi":
      return cevir("Material certificate {0}", t.ad ?? t.rn ?? "");
    // D-074 Faz 4 · PR 22: CoC okuma işi koduyla ("Certificate of conformity CC-0C9D8E7F").
    case "uygunluk_sertifikasi":
      return cevir("Certificate of conformity {0}", t.ad ?? t.rn ?? "");
    // D-074 Faz 4 · PR 23: PO okuma işi koduyla ("Purchase order PUR-6B2C3D4E").
    case "satin_alma":
      return cevir("Purchase order {0}", t.ad ?? t.rn ?? "");
    // D-074 Faz 5 · PR 25: boya raporu okuma işi koduyla ("Coating report COAT-7A1B2C3D").
    case "boya_raporu":
      return cevir("Coating report {0}", t.ad ?? t.rn ?? "");
    default:
      return riser || (t.sn ?? "");
  }
}

/* ------------------------------------------------- riserın Assembly sayfası: kayıt kutusu ------------------------------------------------- */

/** "NDT 3/5 lines": beş hattan NDT raporu ya da "No record available" işareti olanların sayısı. */
export function ndtEtiketi(akis: Pick<MontajAkisBilgisi, "ndtEksik">): EtiketGorunumu {
  const toplam = SET_HATLARI.length;
  return { metin: cevir("NDT {0}/{1} lines", toplam - akis.ndtEksik.length, toplam), ton: akis.ndtEksik.length ? "warn" : "ok" };
}

/**
 * Rapor önceden yüklü, kayıt yok: ilk düğmenin adı ve (dönüştürücü belgeyi okumayacaksa) sarı cümle. PDF ve sayfa aralıksız
 * rapor "Add to queue" alır; resim ya da sayfa aralıklı ("Partial") raporda kayıt açılır ama sıraya girmez.
 */
export function raporYukluGorunumu(asmRaporu: Pick<MontajBelgeCipi, "pdf" | "sayfa">): { dugme: string; uyari: string | null } {
  if (!asmRaporu.pdf) return { dugme: cevir("Open assembly record"), uyari: cevir("This report is not a PDF, so the converter does not read it. Upload the assembly input by hand.") };
  if (asmRaporu.sayfa) return { dugme: cevir("Open assembly record"), uyari: cevir("This PDF carries several risers (pages {0}), so the converter does not read it. Upload the assembly input by hand.", sayfaAraligiMetni(asmRaporu.sayfa)) };
  return { dugme: cevir("Add to queue"), uyari: null };
}

/**
 * Rapor çipinin öneki: belge PDF ise "PDF · ". Resim olarak yüklenmiş raporda (akıştaki yükleme resmi de kabul eder) önek yazılmaz:
 * çip, PDF olmayan dosyaya "PDF" demez.
 */
export const raporCipOneki = (belge: Pick<MontajBelgeCipi, "pdf">): string => (belge.pdf ? "PDF · " : "");

/**
 * "Finishing step" hücresi: kaydın raporu Finishing'in raporu mu. `bag_yok` (riser kuyrukta, Finishing'de rapor yok) arşiv kaydı
 * gibi, `farkli` riser satırındaki etiketle gösterilir; ikisinde kutuların üstünde ayrıca bant vardır (`uyumBandi`). Kayıt yoksa null.
 */
export function finishingEtiketi(uyum: RaporUyumu): EtiketGorunumu | null {
  switch (uyum) {
    case "ayni":
      return { metin: cevir("Assembly report of the Finishing step"), ton: "ok" };
    case "akis_disi":
    case "bag_yok":
      return { metin: cevir("archive record, not part of the Finishing step"), ton: "neutral" };
    case "farkli":
      return { metin: cevir("record of another file"), ton: "warn" };
    default:
      return null;
  }
}

/**
 * "Assembly input" hücresinde kuyruk etiketinin ayrıntısı: sırada "in the converter queue since 14:06" · dönüştürülürken
 * "since 14:47 · held until 15:17" (ikisi gri) · hatada zaman ve tek cümle sebep (kırmızı) · iş yoksa null.
 */
export function kuyrukAyrintisi(k: MontajIsGorunumu, simdi: Date = new Date()): { metin: string; ton: "ink-3" | "bad" } | null {
  const gri = (metin: string) => (metin ? { metin, ton: "ink-3" as const } : null);
  switch (k.donusum) {
    case "sirada":
      return gri(k.siraTs ? cevir("in the converter queue since {0}", siraZamani(k.siraTs, simdi)) : "");
    case "donusturuluyor":
      return gri(tutmaMetni(k));
    case "hata": {
      const metin = [k.donusumHataTs ? kisaTarihSaat(k.donusumHataTs) : null, k.donusumHata ? cevir(k.donusumHata) : null].filter(Boolean).join(" · ");
      return metin ? { metin, ton: "bad" } : null;
    }
    default:
      return null;
  }
}

/**
 * Kayıt kutusunun altındaki bant (`Band` bileşeninin `tip` değeriyle). Girdisi beklenen kayıt: iş hatadaysa kırmızı üç yol; işi
 * yokken sıraya alınamama sebebi varsa sunucunun cümlesi; öteki durumlarda "This record is open …". Girdisiz kayıt ve okunmuş
 * kayıt mavi. Ekranda doldurulan form (P2) için null.
 */
export function kayitBandi(k: Pick<MontajKaydiYaniti, "rn" | "hal" | "kuyruk" | "kuyrukEngeli">): { tip: "bilgi" | "not" | "duvar"; metin: string } | null {
  switch (k.hal) {
    case "girdi_bekliyor":
      if (k.kuyruk.donusum === "hata") return { tip: "duvar", metin: cevir("The converter could not produce the assembly input. Choose one: Retry puts the report back in the converter queue; Upload assembly input (excel) reads an excel you have; Proceed without excel completes the record with the PDF only and the 30 rows stay empty.") };
      if (!k.kuyruk.donusum && k.kuyrukEngeli) return { tip: "not", metin: cevir(k.kuyrukEngeli) };
      return { tip: "not", metin: cevir("This record is open: the Assembly report PDF is uploaded and the assembly input excel is expected. When the excel arrives, from the converter or uploaded here, the 30 rows, the unit identities, the measurements and the approval block are filled and the record is completed. Nothing in the Finishing step waits for it.") };
    case "girdisiz":
      return { tip: "bilgi", metin: cevir("There are no rows for this record yet. Upload the assembly input excel ({0}-assembly-input-EN.xlsx, standard engine) or add the report to the converter queue to read the 30 rows, identities and measurements.", k.rn) };
    case "okundu":
      return { tip: "bilgi", metin: cevir("Rows, identities and measurements below were read from the assembly input excel and are read-only. The excel is a machine reading of the handwritten form: compare a value with the paper before treating it as final. Uploading a new assembly input replaces them as a whole; the PDF stays the record's source.") };
    default:
      return null;
  }
}

/**
 * Kayıt ile Finishing raporunun ayrıştığı iki durumda kutuların üstündeki sarı bant. `bag_yok`: riser kuyrukta, Finishing'de rapor
 * yok (bantla birlikte "Upload Assembly report (PDF)" düğmesi çizilir). `farkli`: Finishing başka bir dosya tutuyor (düğme yok);
 * `finishingRaporu`: bandın altında Finishing'in tuttuğu dosyanın çipi çizilir. Kayıt kutusundaki çip kaydın kendi dosyasıdır;
 * adımı tamamlayan dosya bu durumda yalnız buradan açılır (riser satırından kalkan rapor çipinin yeri).
 */
export function uyumBandi(uyum: RaporUyumu, rn: string): { metin: string; yukleme: boolean; finishingRaporu: boolean } | null {
  if (uyum === "bag_yok") return { metin: cevir("{0} is in the assembly queue and the Finishing step has no Assembly report; this record holds a report that is not the Finishing report. Upload the Assembly report here to complete the Finishing step. If it is the same file, this record stays as it is.", rn), yukleme: true, finishingRaporu: false };
  if (uyum === "farkli") return { metin: cevir("Finishing holds another Assembly report file than this record. The rows below belong to the earlier file. To read the Finishing report, remove this record and press Add to queue."), yukleme: false, finishingRaporu: true };
  return null;
}

/**
 * "Remove" düğmesinin onay cümlesi; kaldırma sunulmuyorsa null. Kayıt yokken yüklü raporda var olan cümle. Rapor kaydında raporu
 * Finishing raporuysa (`ayni`) Finishing adımından söz edilir; arşiv, `bag_yok` ve `farkli` kayıtlarında yalnız kayıt kalkar.
 * Açık form kendi cümlesini alır; kapalı form ve sunucunun kaldırılamaz dediği kayıt (`kaldirilabilir` yanlış) için null.
 */
export function kaldirmaSorusu(v: Pick<MontajRiserYaniti, "rn" | "kayit" | "akis" | "raporUyumu">): string | null {
  const k = v.kayit;
  if (!k) return v.akis.asmRaporu ? cevir("Remove the assembly report and its excel? The removal is kept in the audit log.") : null;
  if (!k.kaldirilabilir || k.hal === "form_kapali") return null;
  if (k.tur === "form") return cevir("Remove the assembly form of {0}? Everything entered on it is deleted. The removal is kept in the audit log.", v.rn);
  return v.raporUyumu === "ayni"
    ? cevir("Remove the Assembly report of {0}? Its assembly record and the rows read from the excel are removed with it. The files stay in the archive and the removal is kept in the audit log. The Finishing step of {0} waits for the report again.", v.rn)
    : cevir("Remove the assembly record of {0}? The rows read from the excel are removed. The files stay in the archive and the removal is kept in the audit log.", v.rn);
}

/** Seçilen dosyanın adı başka bir riser söylüyorsa o riser; ad riser vermiyorsa ya da sayfanın riserını veriyorsa null. */
export function dosyaRiserFarki(dosyaAdi: string, rn: string): string | null {
  const okunan = montajRnBul(dosyaAdi);
  return okunan && okunan !== rn.trim().toUpperCase() ? okunan : null;
}

/** Yüklemeden önce sorulacak cümle (dosya adı başka riser diyor); soru gerekmiyorsa null. Yeni sunucu kuralı değil, ekran korumasıdır. */
export function dosyaRiserSorusu(dosyaAdi: string, rn: string): string | null {
  const baska = dosyaRiserFarki(dosyaAdi, rn);
  return baska ? cevir("The file name says {0}, but this is the Assembly page of {1}. Upload it for {1}?", baska, rn) : null;
}

/* ------------------------------------------------ riserın Assembly sayfası: okunmuş kayıt ------------------------------------------------ */

/** Girdinin kaynağı: "converter" ya da "by hand". */
export const girdiKaynagiAdi = (kaynak: "elle" | "donusturucu"): string => (kaynak === "donusturucu" ? cevir("converter") : cevir("by hand"));

/** "Form edition" hücresindeki gri metin: A basımında 29, B ve C basımında (ve basım bilinmiyorsa) 30 basılı satır. */
export const basiliSatirMetni = (basim: Fm12Basimi | null): string => cevir("{0} printed rows", fm12SatirSayisi(basim));

/** Eksik taranmış sayfa: "Pages" hücresindeki etiket ve kutunun altındaki sarı bant; eksik sayfa yoksa null. */
export function eksikSayfaGorunumu(sayfalar: readonly number[]): { etiket: EtiketGorunumu; bant: string } | null {
  if (!sayfalar.length) return null;
  const metin = eksikSayfaMetni(sayfalar);
  return {
    etiket: { metin: cevir("page not scanned: {0}", metin), ton: "warn" },
    bant: cevir("Not in the scanned PDF: page {0}. The rows printed there are empty and listed as conflicts.", metin),
  };
}

/** Assembly SN'in çözüldüğü kimlik uzayının etiketi; bilinmeyen değer için null. */
export function uzayEtiketi(uzay: string | null | undefined): EtiketGorunumu | null {
  switch (uzay) {
    case "ASSEMBLY":
      return { metin: cevir("same as the MRB Assembly SN"), ton: "ok" };
    case "MT_WELDMENT":
      return { metin: cevir("MT weldment SN in the MRB"), ton: "warn" };
    case "NO_MRB":
      return { metin: cevir("No MRB content"), ton: "neutral" };
    case "UNRESOLVED":
      return { metin: cevir("not found in the riser's MRB"), ton: "warn" };
    default:
      return null;
  }
}

/** Girdideki çelişkiler (`MAPPING_REPORT`'un CONFLICT satırları): kanıttır, silinmez. */
export const celiskiListesi = (okuma: Pick<MontajOkuma, "rapor"> | null | undefined): string[] => (okuma?.rapor ?? []).filter((r) => r.tip === "CONFLICT").map((r) => r.mesaj);

const DURUM_TONU: Record<MontajSatirDurumu, Ton> = { takildi: "ok", listede: "info", sahada: "neutral", takilmadi: "neutral", uygulanmaz: "neutral" };

/** Satır durumu etiketi; söz alan paketindeki `MONTAJ_DURUM_SOZU`'ndan gelir. Boş ya da tanınmayan durum "undetermined" (sarı). */
export function satirDurumEtiketi(durum: string | null): EtiketGorunumu {
  const d = MONTAJ_SATIR_DURUMLARI.find((x) => x === durum);
  return d ? { metin: cevir(MONTAJ_DURUM_SOZU[d]), ton: DURUM_TONU[d] } : { metin: cevir("undetermined"), ton: "warn" };
}

/** Kimlikleri formda liste olarak yazılan satır mı (cıvata 9, pul 10, locking insert 11, buoyancy 30): birimleri alt alta çizilir. */
export const listeSatiriMi = (sira: number): boolean => Boolean(fm12Satiri(sira)?.liste);

/** Birimin dolu temiz alanları, sabit sırada (SN · PN · Heat · PO · WO · Tag · Other); boş ve yalnız boşluk olan alan atlanır. */
export function birimParcalari(b: Partial<Pick<MontajBirimYaniti, "sn" | "pn" | "heat" | "po" | "wo" | "etiket" | "diger">>): { ad: string; deger: string }[] {
  const alanlar: [string, string | null | undefined][] = [["SN", b.sn], ["PN", b.pn], ["Heat", b.heat], ["PO", b.po], ["WO", b.wo], [cevir("Tag"), b.etiket], [cevir("Other"), b.diger]];
  return alanlar.flatMap(([ad, v]) => (v?.trim() ? [{ ad, deger: v.trim() }] : []));
}

/** Birimin etiketleri: rol (Choke / Kill; hat adı çevrilmez) · montajdan sonra söküldü · okuma belirsiz. */
export function birimEtiketleri(b: Pick<MontajBirimYaniti, "rol" | "durum" | "belirsiz">): EtiketGorunumu[] {
  const etiketler: EtiketGorunumu[] = [];
  if (b.rol) etiketler.push({ metin: b.rol === "C" ? "Choke" : "Kill", ton: "info" });
  if (b.durum === "sokuldu") etiketler.push({ metin: cevir("removed after assembly"), ton: "warn" });
  if (b.belirsiz) etiketler.push({ metin: cevir("uncertain reading"), ton: "warn" });
  return etiketler;
}

/** Birimin ipucu (`title`): "As written: …" ve varsa notu; ikisi de yoksa undefined. */
export function birimIpucu(b: Pick<MontajBirimYaniti, "yazim" | "not">): string | undefined {
  return [b.yazim?.trim() ? `${cevir("As written:")} ${b.yazim.trim()}` : null, b.not?.trim() || null].filter(Boolean).join(" · ") || undefined;
}

/** Çelişki metinleri harf ve rakam olarak karşılaştırılır (söküm kaydındaki kural). */
const sade = (m: string): string => m.toLocaleLowerCase("tr").replace(/[^\p{L}\p{N}]+/gu, "");

/**
 * Satırın altındaki CONFLICT bantları: önce satırın çelişkisi, sonra birim çelişkileri ("unit {0}: {1}"). Satır çelişkisinin
 * tekrarı olan birim çelişkisi yazılmaz.
 */
export function satirCeliskileri(x: { celiski: string | null; birimler: readonly Pick<MontajBirimYaniti, "seq" | "celiski">[] }): string[] {
  const satir = x.celiski?.trim() ? sade(x.celiski) : "";
  const tekrar = (c: string) => Boolean(satir) && (satir.includes(sade(c)) || sade(c).includes(satir));
  return [
    ...(x.celiski?.trim() ? [x.celiski] : []),
    ...x.birimler.flatMap((b) => (b.celiski?.trim() && !tekrar(b.celiski) ? [cevir("unit {0}: {1}", b.seq, b.celiski)] : [])),
  ];
}

/** Ölçü kutusunun basılı terimleri (çevrilmez). */
export const OLCU_TIP_ADI: Readonly<Record<MontajOlcumTipi, string>> = { stickout: "Pin stick out", gap: "Box gap" };
export const OLCU_HAT_ADI: Readonly<Record<MontajOlcumHatti, string>> = { CH: "Choke", KL: "Kill", BST: "Booster", HYD: "Hydraulic" };

export interface OlcuHucresi {
  /** temiz değer; yoksa null */
  deger: number | null;
  /** formda yazıldığı gibi; boşsa null */
  yazim: string | null;
  /** değer yok ama yazım var: iki değer yazılmış, belirsiz okunmuş ya da okunamamış ("not resolved" + altında yazım) */
  cozulmedi: boolean;
  /** söküm öncesi ölçü (yalnız gösterilir; tolerans yargısı yok); yoksa null ve satırı çizilmez */
  onceki: number | null;
}

/** Bir ölçü hücresi. Söküm öncesi değer yanıttaki alandan gelir; eşlemeyi sunucu yapar. */
export function olcuHucresi(o: Pick<MontajOlcumYaniti, "deger" | "yazim" | "sokumOncesi"> | null | undefined): OlcuHucresi {
  const yazim = o?.yazim?.trim() ? o.yazim.trim() : null;
  const deger = o?.deger ?? null;
  return { deger, yazim, cozulmedi: deger == null && yazim !== null, onceki: o?.sokumOncesi ?? null };
}

/**
 * "Stick outs and box gaps" kutusunun iki grubu, her birinde dört hücre (CH, KL, BST, HYD sırası). Grup başlığı basılı terim ve
 * birim ekidir: girdi mm diyorsa "(mm)", değilse "(in)". Ölçümü olmayan hücre boş çizilir ("—").
 */
export function olcuGruplari(
  olcumler: readonly MontajOlcumYaniti[],
  olcuBirimi: string | null | undefined,
): { tip: MontajOlcumTipi; baslik: string; hucreler: { hat: MontajOlcumHatti; ad: string; hucre: OlcuHucresi }[] }[] {
  const ek = olcuBirimi === "mm" ? "(mm)" : "(in)";
  return MONTAJ_OLCUM_TIPLERI.map((tip) => ({
    tip,
    baslik: `${OLCU_TIP_ADI[tip]} ${ek}`,
    hucreler: MONTAJ_OLCUM_HATLARI.map((hat) => ({ hat, ad: OLCU_HAT_ADI[hat], hucre: olcuHucresi(olcumler.find((o) => o.tip === tip && o.hat === hat)) })),
  }));
}

/** Ölçü kutusunun altındaki gri satır: iki ölçü grubunun formdaki yazımı, birim ve (formda yazmıyorsa) inç varsayımı. */
export function olcuAltMetni(form: Pick<MontajOkumaFormu, "olculerYazim" | "olcuBirimi" | "olcuBirimiCikarim">): string {
  const metin = `${cevir("As written:")} ${form.olculerYazim.stickout || "—"} · ${form.olculerYazim.gap || "—"}. ${cevir("Unit:")} ${form.olcuBirimi || cevir("not recorded")}`;
  return form.olcuBirimiCikarim ? `${metin} · ${cevir("unit not written on the form, inch assumed")}` : metin;
}

/** Onay bloğunda imza: "yes" · "no" · satır formda boşsa "—". */
export const imzaMetni = (imzali: boolean | null | undefined): string => (imzali === true ? cevir("yes") : imzali === false ? cevir("no") : "—");

/** Onay bloğunun üç satırı, formun basılı adlarıyla (çevrilmez). Hiçbiri zorunlu değildir; boş satır uyarı üretmez. */
export function onaySatirlari(onay: MontajOnay | null): { baslik: string; ad: string | null; tarih: string | null; tarihYazim: string | null; imza: string }[] {
  const satirlar = [["Assembly Completion", onay?.tamamlama], ["Manager Review", onay?.yonetici], ["QA/QC Review", onay?.kalite]] as const;
  return satirlar.map(([baslik, s]) => ({ baslik, ad: s?.ad?.trim() || null, tarih: s?.tarih ?? null, tarihYazim: s?.tarihYazim?.trim() || null, imza: imzaMetni(s?.imzali) }));
}

/* ------------------------------------------------------- iki adımlı akışlar ------------------------------------------------------- */

const hataMetni = (e: unknown): string => (e instanceof Error && e.message ? e.message : cevir("Operation failed"));

export type YuklemeAdimi =
  | { adim: "rapor_yukle" }
  | { adim: "kayit_ac" }
  | { adim: "bitti"; mesaj: string | null }
  | { adim: "hata"; mesaj: string; kalan: "kayitsiz_bag" | "yok" };

/**
 * Akıştaki riserda rapor yüklemesinin sırası: önce Finishing bağı (var olan uç), sonra montaj kaydı (`ac`). `onceki` null: ilk
 * adım. Rapor yüklenemediyse hiçbir şey kalmaz. Kayıt açılamadıysa rapor yüklü kalır ("kayıtsız bağ": zararsız; sayfa "rapor
 * önceden yüklü" hâline düşer ve "Add to queue" sunar). Kayıt açıldı ama sıraya girmediyse sebep cümlesi `mesaj`dadır (çevrilmiş).
 * `hata` alanı çağıranın yakaladığı hata mesajıdır (`ApiHata.message`; çevrilmiş gelir).
 */
export function yuklemeAdimi(
  onceki: null | { adim: "rapor_yukle"; ok: boolean; hata?: string } | { adim: "kayit_ac"; ok: boolean; hata?: string; kuyrukEngeli?: string | null },
): YuklemeAdimi {
  if (!onceki) return { adim: "rapor_yukle" };
  if (onceki.adim === "rapor_yukle") return onceki.ok ? { adim: "kayit_ac" } : { adim: "hata", mesaj: onceki.hata || cevir("Operation failed"), kalan: "yok" };
  if (!onceki.ok) return { adim: "hata", mesaj: cevir("The Assembly report is uploaded, but its assembly record could not be opened: {0} Use Add to queue on this page.", onceki.hata || cevir("Operation failed")), kalan: "kayitsiz_bag" };
  return { adim: "bitti", mesaj: onceki.kuyrukEngeli ? cevir(onceki.kuyrukEngeli) : null };
}

export type KaldirmaAdimi =
  | { adim: "kayit_kaldir" }
  | { adim: "bag_kaldir" }
  | { adim: "bitti" }
  | { adim: "hata"; mesaj: string; kalan: "kayitsiz_bag" | "yok" };

/**
 * "Remove" sırası: önce montaj kaydı, sonra (yanıtta `akisBagi` doğruysa ve kayıt rapor türündeyse) Finishing bağı. İkinci çağrıya
 * `raporUyumu`'na değil sunucunun `akisBagi` yanıtına bakılarak karar verilir (karar anındaki gerçek durum odur). Form türünde
 * ikinci çağrı yapılmaz. Bağ kaldırılamazsa "kayıtsız bağ" kalır; oradaki "Remove" raporu kaldırır.
 */
export function kaldirmaAdimi(
  tur: "rapor" | "form",
  onceki: null | { adim: "kayit_kaldir"; ok: boolean; akisBagi?: boolean; hata?: string } | { adim: "bag_kaldir"; ok: boolean; hata?: string },
): KaldirmaAdimi {
  if (!onceki) return { adim: "kayit_kaldir" };
  if (onceki.adim === "kayit_kaldir") {
    if (!onceki.ok) return { adim: "hata", mesaj: onceki.hata || cevir("Operation failed"), kalan: "yok" };
    return tur === "rapor" && onceki.akisBagi ? { adim: "bag_kaldir" } : { adim: "bitti" };
  }
  return onceki.ok ? { adim: "bitti" } : { adim: "hata", mesaj: onceki.hata || cevir("Operation failed"), kalan: "kayitsiz_bag" };
}

export type YuklemeSonucu = Extract<YuklemeAdimi, { adim: "bitti" | "hata" }>;
export type KaldirmaSonucu = Extract<KaldirmaAdimi, { adim: "bitti" | "hata" }>;

/**
 * İki adımlı rapor yüklemesini `yuklemeAdimi` sırasıyla yürütür ve sonucu döner (hata fırlatmaz). `raporYukle`: var olan uca
 * dosya yüklemesi (`dosyaYukle(...akisRaporIstegi(...))`); `kayitAc`: `montajAc(rn)`. Çağıran sonuçtan sonra sayfayı tazeler.
 */
export async function yuklemeyiYurut(is: { raporYukle: () => Promise<unknown>; kayitAc: () => Promise<{ kuyrukEngeli?: string | null }> }): Promise<YuklemeSonucu> {
  let adim = yuklemeAdimi(null);
  for (;;) {
    if (adim.adim === "bitti" || adim.adim === "hata") return adim;
    if (adim.adim === "rapor_yukle") {
      try {
        await is.raporYukle();
        adim = yuklemeAdimi({ adim: "rapor_yukle", ok: true });
      } catch (e) {
        adim = yuklemeAdimi({ adim: "rapor_yukle", ok: false, hata: hataMetni(e) });
      }
    } else {
      try {
        const y = await is.kayitAc();
        adim = yuklemeAdimi({ adim: "kayit_ac", ok: true, kuyrukEngeli: y.kuyrukEngeli ?? null });
      } catch (e) {
        adim = yuklemeAdimi({ adim: "kayit_ac", ok: false, hata: hataMetni(e) });
      }
    }
  }
}

/**
 * "Remove"u `kaldirmaAdimi` sırasıyla yürütür ve sonucu döner (hata fırlatmaz). `kayitKaldir`: `montajKaldir(id)`;
 * `bagKaldir`: `asmRaporKaldir(rn)`. Çağıran sonuçtan sonra sayfayı tazeler.
 */
export async function kaldirmayiYurut(tur: "rapor" | "form", is: { kayitKaldir: () => Promise<{ akisBagi?: boolean }>; bagKaldir: () => Promise<unknown> }): Promise<KaldirmaSonucu> {
  let adim = kaldirmaAdimi(tur, null);
  for (;;) {
    if (adim.adim === "bitti" || adim.adim === "hata") return adim;
    if (adim.adim === "kayit_kaldir") {
      try {
        const y = await is.kayitKaldir();
        adim = kaldirmaAdimi(tur, { adim: "kayit_kaldir", ok: true, akisBagi: Boolean(y.akisBagi) });
      } catch (e) {
        adim = kaldirmaAdimi(tur, { adim: "kayit_kaldir", ok: false, hata: hataMetni(e) });
      }
    } else {
      try {
        await is.bagKaldir();
        adim = kaldirmaAdimi(tur, { adim: "bag_kaldir", ok: true });
      } catch (e) {
        adim = kaldirmaAdimi(tur, { adim: "bag_kaldir", ok: false, hata: hataMetni(e) });
      }
    }
  }
}

/* ---------------------------------------------------------- API çağrıları ---------------------------------------------------------- */

/** "Assembly records" listesi ve dönüştürücü kutusu (sessiz yenileme bunu çağırır; ilk yükleme `useVeri(MONTAJ_UCU.liste)`). */
export const montajListesiGetir = () => api.get<MontajListesi>(MONTAJ_UCU.liste);
/** Finishing'deki rapordan kayıt açar: yüklemeden sonra kendiliğinden ve "Add to queue" / "Open assembly record" ile. Yinelenebilir. */
export const montajAc = (rn: string) => api.post<MontajAcYaniti>(MONTAJ_UCU.ac(rn));
/** Kuyruk işlemi: sıraya ekle ("Add to queue", hatadan "Retry") ya da sıradan çıkar ("Remove from queue"). */
export const montajSiraIslemi = (id: string, islem: "ekle" | "cikar") => api.post<MontajSiraYaniti>(MONTAJ_UCU.sira(id), { islem });
/** "Proceed without excel": bekleyen kaydı assembly girdisi olmadan tamamlar. */
export const montajGirdisiz = (id: string) => api.post<{ ok: true }>(MONTAJ_UCU.girdisiz(id));
/** "Remove"un ilk adımı: montaj kaydını kaldırır; Finishing bağına dokunmaz. */
export const montajKaldir = (id: string) => api.post<MontajKaldirYaniti>(MONTAJ_UCU.kaldir(id));
/** Var olan uç: Finishing'deki Assembly raporu bağını kaldırır ("Remove"un ikinci adımı ve kayıtsız yüklü raporun "Remove"u). */
export const asmRaporKaldir = (rn: string) => api.post<unknown>(MONTAJ_UCU.asmRaporKaldir(rn));
/** Var olan uç: okunmayan eski excel ekini kaldırır ("Remove excel"). */
export const asmExcelKaldir = (rn: string) => api.post<unknown>(MONTAJ_UCU.asmExcelKaldir(rn));
